<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import LiveWhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/LiveWhatsAppCampaign/LiveWhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const [showLiveWhatsAppCampaignDialog, toggleLiveWhatsAppCampaignDialog] =
  useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const LiveWhatsAppCampaigns = computed(
  () => getters['campaigns/getLiveWhatsAppCampaigns'].value
);

// v87: apagar so id 1 + filtros por data e por criador
const currentUser = useMapGetter('getCurrentUser');
const isSuperAdmin = computed(() => currentUser.value?.id === 1);

const filterFrom = ref('');
const filterTo = ref('');
const filterSenderId = ref('');

const senderOptions = computed(() => {
  const map = new Map();
  LiveWhatsAppCampaigns.value?.forEach(c => {
    if (c.sender?.id) map.set(c.sender.id, c.sender.name || c.sender.available_name);
  });
  return Array.from(map, ([value, label]) => ({ value, label }));
});

const filteredCampaigns = computed(() => {
  let list = LiveWhatsAppCampaigns.value || [];
  if (filterSenderId.value) {
    list = list.filter(c => String(c.sender?.id) === String(filterSenderId.value));
  }
  if (filterFrom.value) {
    const from = new Date(`${filterFrom.value}T00:00:00-03:00`);
    list = list.filter(c => new Date(c.created_at) >= from);
  }
  if (filterTo.value) {
    const to = new Date(`${filterTo.value}T23:59:59-03:00`);
    list = list.filter(c => new Date(c.created_at) <= to);
  }
  return list;
});

const hasNoLiveWhatsAppCampaigns = computed(
  () => LiveWhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.LIVE_WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.LIVE_WHATSAPP.NEW_CAMPAIGN')"
    @click="toggleLiveWhatsAppCampaignDialog()"
    @close="toggleLiveWhatsAppCampaignDialog(false)"
  >
    <template #action>
      <LiveWhatsAppCampaignDialog
        v-if="showLiveWhatsAppCampaignDialog"
        @close="toggleLiveWhatsAppCampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="!hasNoLiveWhatsAppCampaigns">
      <!-- v87: filtros por data de criacao e por criador -->
      <div class="flex flex-wrap items-end gap-3 mb-4">
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11">De</label>
          <input
            v-model="filterFrom"
            type="date"
            class="h-9 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11">Até</label>
          <input
            v-model="filterTo"
            type="date"
            class="h-9 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-n-slate-11">Criada por</label>
          <select
            v-model="filterSenderId"
            class="h-9 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 text-sm text-n-slate-12 outline-none min-w-44"
          >
            <option value="">Todos</option>
            <option v-for="opt in senderOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <p class="text-xs text-n-slate-11 pb-2">
          {{ filteredCampaigns.length }} campanha(s)
        </p>
      </div>
      <CampaignList
        :campaigns="filteredCampaigns"
        is-live-whatsapp-type
        :can-delete="isSuperAdmin"
        @delete="handleDelete"
      />
    </template>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.LIVE_WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.LIVE_WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
